import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, Globe, Lock, Phone, RefreshCw, Share2 } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Capacitor } from "@capacitor/core";
import { getRequest, putRequest } from "../helpers/api.helper";
import { buildLoginCredentialsMessage } from "../helpers/loginCredentials";
import { normalizeWhatsAppNumber, openWhatsAppChatDirect } from "../helpers/whatsapp.helper";
import { openNativeWhatsAppChat } from "../helpers/nativeShare";
import { clearDashboard } from "../store/dashboard.slice";
import { RootState } from "../store";

type Profile = { martialId: string; fullName: string; password?: string; mobileNumber?: string; countryCode?: string; visibleToAll: string | boolean };
type Stats = {
  totalContactViewers: number;
  bureauContactViewers: number;
  memberContactViewers: number;
  totalEarnings: number;
  contactsViewedByUser: number;
  contactViewCost: number;
};
const money = (paise: number) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(paise / 100);

export default function ProfilePrivacyAndInsights({ profile, onVisibilityChanged }: {
  profile: Profile;
  onVisibilityChanged: (isPublic: boolean, contactPrivacy: string) => void;
}) {
  const dispatch = useDispatch();
  const bureau = useSelector((state: RootState) => state.bureau.data);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showBenefits, setShowBenefits] = useState(false);
  const [error, setError] = useState("");
  const requestSequence = useRef(0);
  const savingRef = useRef(false);
  const isPublic = [true, "true", "1"].includes(profile.visibleToAll);
  const contactNumber = normalizeWhatsAppNumber(profile.mobileNumber, profile.countryCode);
  const base = `/bureau/profile/${encodeURIComponent(profile.martialId)}`;

  const load = useCallback(async () => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError("");
    try {
      const response = await getRequest(`${base}/contact-stats`);
      if (sequence !== requestSequence.current) return;
      if (response.success && response.data) setStats(response.data);
      else setError(response.message || "Unable to load profile contact statistics.");
    } catch {
      if (sequence === requestSequence.current) setError("Unable to load profile contact statistics.");
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [base]);
  useEffect(() => {
    void load();
    const refresh = () => { void load(); };
    window.addEventListener("bureau-wallet-updated", refresh);
    return () => {
      requestSequence.current += 1;
      window.removeEventListener("bureau-wallet-updated", refresh);
    };
  }, [load]);

  const updateVisibility = async (next: boolean) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    try {
      const response = await putRequest(`${base}/visibility`, { isPublic: next });
      if (!response.success) { toast.error(response.message || "Unable to update profile visibility."); return; }
      onVisibilityChanged(next, response.data.contactPrivacy);
      dispatch(clearDashboard());
      toast.success(next ? "Profile is now Public." : "Profile is now Private. Other users cannot view its contact details.");
    } catch {
      toast.error("Unable to update profile visibility.");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const openProfileWhatsApp = async () => {
    if (!contactNumber) {
      toast.error("A valid phone number is missing for this profile.");
      return;
    }
    try {
      if (Capacitor.getPlatform() === "android") {
        await openNativeWhatsAppChat(contactNumber, "");
      } else {
        openWhatsAppChatDirect(contactNumber, "");
      }
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") return;
      toast.error(error instanceof Error ? error.message : "Unable to open WhatsApp.");
    }
  };

  const shareLogin = async () => {
    const phoneNumber = normalizeWhatsAppNumber(profile.mobileNumber, profile.countryCode);
    if (!phoneNumber) {
      toast.error("A valid phone number is missing for this profile.");
      return;
    }
    const password = profile.password && !/^\$2[aby]\$\d{2}\$/.test(profile.password)
      ? profile.password
      : "Use Forgot Password on the login page to set or reset your password.";
    try {
      const text = buildLoginCredentialsMessage({
        fullName: profile.fullName, martialId: profile.martialId, password,
        websiteUrl: bureau?.websiteUrl, websiteLinks: bureau?.websiteLinks,
      });
      if (Capacitor.getPlatform() === "android") {
        await openNativeWhatsAppChat(phoneNumber, text);
      } else {
        openWhatsAppChatDirect(phoneNumber, text);
      }
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") return;
      toast.error(error instanceof Error ? error.message : "Unable to share login details.");
    }
  };

  return (
    <section className="space-y-4 rounded-2xl border border-purple-100 bg-purple-50/40 p-4" aria-label="Profile visibility and contact activity">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-base font-bold text-gray-900">Profile Status</h2>
        <button type="button" onClick={() => void shareLogin()} className="inline-flex items-center gap-2 rounded-lg border border-purple-200 bg-white px-3 py-2 text-sm font-semibold text-purple-700">
          <Share2 size={16} aria-hidden="true" /> Share Login Details
        </button>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-gray-900">{contactNumber ? `+${contactNumber}` : profile.mobileNumber || "Mobile number unavailable"}</span>
          <button type="button" onClick={() => void openProfileWhatsApp()} disabled={!contactNumber} aria-label="WhatsApp this profile" title="Open WhatsApp" className="inline-flex items-center justify-center rounded-lg border border-emerald-200 bg-white p-2 text-emerald-600 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50">
            <FaWhatsapp size={18} aria-hidden="true" />
          </button>
          {contactNumber && (
            <a href={`tel:+${contactNumber}`} aria-label="Call this profile" title="Call" className="inline-flex items-center justify-center rounded-lg border border-purple-200 bg-white p-2 text-purple-700 hover:bg-purple-50">
              <Phone size={18} aria-hidden="true" />
            </a>
          )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <button type="button" disabled={saving} aria-pressed={isPublic} onClick={() => void updateVisibility(true)} className={`flex items-center justify-center gap-2 rounded-xl border-2 p-3 text-sm font-semibold disabled:opacity-50 ${isPublic ? "border-emerald-500 bg-emerald-50 text-emerald-800" : "border-gray-200 bg-white text-gray-600"}`}>
          <Globe size={18} aria-hidden="true" /> Public
        </button>
        <button type="button" disabled={saving} aria-pressed={!isPublic} onClick={() => void updateVisibility(false)} className={`flex items-center justify-center gap-2 rounded-xl border-2 p-3 text-sm font-semibold disabled:opacity-50 ${!isPublic ? "border-purple-500 bg-purple-100 text-purple-800" : "border-gray-200 bg-white text-gray-600"}`}>
          <Lock size={18} aria-hidden="true" /> Private
        </button>
      </div>
      {saving ? (
        <p className="text-sm text-gray-600">Updating visibility…</p>
      ) : null}
      <button type="button" onClick={() => setShowBenefits((shown) => !shown)} aria-expanded={showBenefits} className="inline-flex items-center gap-2 text-sm font-semibold text-purple-700 hover:underline">
        {showBenefits ? "Hide Benefits" : "View Benefits"}
        <ChevronDown size={18} aria-hidden="true" className={`transition-transform ${showBenefits ? "rotate-180" : ""}`} />
      </button>
      {showBenefits && (isPublic ? (
        <div lang="te" className="space-y-3 text-sm leading-relaxed text-gray-600">
          <p className="font-semibold text-emerald-700">Congratulations...</p>
          <p>ఈ ప్రొఫైల్ స్టేటస్ పబ్లిక్ లో ఉన్నది. ఈ ప్రొఫైల్ మీద మీకు ఇన్కమ్ స్టార్ట్ అయ్యింది. ఇక నుండి అదర్ మ్యారేజ్ బ్యూరో ఓనర్స్ లేదా యూజర్స్ ఈ ప్రొఫైల్ కాంటాక్ట్ డీటెయిల్స్ క్లిక్ చేసినప్పుడు మీ వాలెట్ లోకి 50/- క్రెడిట్ అవుతుంది.</p>
          <p>Example :- ఈ ప్రొఫైల్ కాంటాక్ట్ డీటెయిల్స్ 100 members చూస్తే 5000/- అమౌంట్ మీ వాలెట్ లోకి క్రెడిట్ అవుతుంది.</p>
          <p>మరియు ఈ ప్రొఫైల్ మీ బ్యూరో పరిధిలోనే ఉంటుంది ఎవ్వరు కూడా ఈ ప్రొఫైల్ ని కాపీ చేయలేరు. వేరే బ్యూరో లో ఈ ప్రొఫైల్ క్రియేట్ చేయలేరు.</p>
          <p className="font-semibold">ఈ ప్రొఫైల్ మీద సర్వహక్కులు మీకే ఉంటాయి.</p>
        </div>
      ) : (
        <div lang="te" className="space-y-3 text-sm leading-relaxed text-gray-600">
          <p>ఈ ప్రొఫైల్ ప్రైవేట్ లో ఉన్నది. ఈ ప్రొఫైల్ కాంటాక్ట్ డీటెయిల్స్ ఎవరికి కనిపించవు. ఈ ప్రొఫైల్ మీద మీకు ఎలాంటి ఇన్కమ్ రాదు.</p>
          <p>ఈ ప్రొఫైల్ మీద మీకు ఇన్కమ్ రావాలి అనుకుంటే పబ్లిక్ లో పెట్టండి.</p>
          <details className="group">
            <summary className="flex cursor-pointer list-none items-center gap-2 font-semibold text-gray-900 [&::-webkit-details-marker]:hidden">
              పబ్లిక్ ప్రొఫైల్ ఉపయోగాలు
              <ChevronDown size={18} aria-hidden="true" className="shrink-0 transition-transform group-open:rotate-180" />
            </summary>
            <div className="mt-3 space-y-3">
              <p>అదర్ మ్యారేజ్ బ్యూరో ఓనర్స్ లేదా యూజర్స్ ఈ ప్రొఫైల్ కాంటాక్ట్ డీటెయిల్స్ క్లిక్ చేసినప్పుడు మీ వాలెట్ లోకి 50/- క్రెడిట్ అవుతుంది.</p>
              <p>ఉదాహరణ:- ఈ ప్రొఫైల్ కాంటాక్ట్ డీటెయిల్స్ 100 మెంబర్స్ చూసినట్లయితే ఈ ప్రొఫైల్ మీద మీకు 5000/- క్రెడిట్ అవుతుంది, మరియు ఈ ప్రొఫైల్ మీ బ్యూరో పరిధిలోనే ఉంటుంది ఎవ్వరు కూడా ఈ ప్రొఫైల్ ని కాపీ చేయలేరు. వేరే బ్యూరో లో ఈ ప్రొఫైల్ క్రియేట్ చేయలేరు.</p>
              <p className="font-semibold">ఈ ప్రొఫైల్ మీద సర్వహక్కులు మీకే ఉంటాయి.</p>
            </div>
          </details>
        </div>
      ))}
      <div className="flex items-center justify-between gap-2 border-t border-purple-100 pt-4">
        <h3 className="font-semibold text-gray-900">Contact Views &amp; Earnings</h3>
        <button type="button" disabled={loading} onClick={() => void load()} aria-label="Refresh profile contact statistics" title="Refresh statistics" className="rounded-full p-2 text-purple-700 hover:bg-purple-100 disabled:opacity-50">
          <RefreshCw size={17} className={loading ? "animate-spin" : ""} aria-hidden="true" />
        </button>
      </div>
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      {!stats && loading && <p className="text-sm text-gray-500">Loading contact activity…</p>}
      {stats && (
        <>
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            {([
              ["Contact viewers", String(stats.totalContactViewers)],
              ["Total profile earnings", money(stats.totalEarnings)],
              ["Other contacts viewed by this user", String(stats.contactsViewedByUser)],
            ] as [string, string][]).map(([label, value]) => (
              <div key={label} className="min-w-0 rounded-xl border border-purple-100 bg-white p-2 sm:p-3">
                <p className="text-xs text-gray-600">{label}</p>
                <p className="mt-2 break-words text-base font-bold text-purple-800 sm:text-xl">{value}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
